import React from 'react';
import Icon from 'components/AppIcon';

const VolatilityIndicator = ({ economyState, loading }) => {
  if (loading) {
    return (
      <div className="card p-6 animate-pulse">
        <div className="h-4 bg-surface-700 rounded w-1/2 mb-4" />
        <div className="h-24 bg-surface-700 rounded" />
      </div>
    );
  }

  const inflationIndex = parseFloat(economyState?.inflation_index || 0);
  const economyModifier = parseFloat(economyState?.economy_modifier || 1.0);
  const isRecession = economyState?.recession_state || false;
  const marginDisabled = economyState?.margin_disabled || false;
  const ipoFrozen = economyState?.ipo_frozen || false;

  // Market heat index: 0-100 based on inflation and economy modifier
  const heatIndex = Math.min(100, Math.max(0, (inflationIndex * 200) + ((1 - economyModifier) * 100)));

  const getHeatColor = (heat) => {
    if (heat < 25) return { text: 'text-success', bg: 'bg-success', label: 'Cool', desc: 'Stable market conditions' };
    if (heat < 50) return { text: 'text-warning', bg: 'bg-warning', label: 'Warm', desc: 'Moderate volatility' };
    if (heat < 75) return { text: 'text-orange-400', bg: 'bg-orange-400', label: 'Hot', desc: 'High volatility detected' };
    return { text: 'text-error', bg: 'bg-error', label: 'Critical', desc: 'Extreme market stress' };
  };

  const heatConfig = getHeatColor(heatIndex);

  const volatilityThreshold = 0.05;
  const maxAllowedChange = 0.08;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-foreground flex items-center gap-2">
          <Icon name="Activity" size={18} className="text-primary" />
          Market Volatility
        </h3>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${heatConfig?.text} bg-surface-700`}>
          {heatConfig?.label}
        </span>
      </div>
      {/* Heat Index Gauge */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-muted mb-1">
          <span>Heat Index</span>
          <span className={`font-bold ${heatConfig?.text}`}>{heatIndex?.toFixed(1)}/100</span>
        </div>
        <div className="h-3 bg-surface-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${heatConfig?.bg}`}
            style={{ width: `${heatIndex}%` }}
          />
        </div>
        <p className="text-xs text-muted mt-1">{heatConfig?.desc}</p>
      </div>
      {/* Bounded Volatility Controls */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-surface-700 rounded-lg p-3">
          <p className="text-xs text-muted mb-1">Volatility Threshold</p>
          <p className="text-sm font-bold text-foreground">{(volatilityThreshold * 100)?.toFixed(0)}%</p>
          <p className="text-xs text-muted">per tick</p>
        </div>
        <div className="bg-surface-700 rounded-lg p-3">
          <p className="text-xs text-muted mb-1">Max Allowed Change</p>
          <p className="text-sm font-bold text-foreground">{(maxAllowedChange * 100)?.toFixed(0)}%</p>
          <p className="text-xs text-muted">dampened</p>
        </div>
        <div className="bg-surface-700 rounded-lg p-3">
          <p className="text-xs text-muted mb-1">Inflation Index</p>
          <p className={`text-sm font-bold ${inflationIndex > 0.15 ? 'text-error' : 'text-success'}`}>
            {(inflationIndex * 100)?.toFixed(2)}%
          </p>
        </div>
        <div className="bg-surface-700 rounded-lg p-3">
          <p className="text-xs text-muted mb-1">Economy Modifier</p>
          <p className={`text-sm font-bold ${economyModifier < 0.8 ? 'text-error' : 'text-success'}`}>
            {economyModifier?.toFixed(2)}x
          </p>
        </div>
      </div>
      {/* Status Flags */}
      <div className="space-y-1.5">
        {isRecession && (
          <div className="flex items-center gap-2 px-3 py-2 bg-error/10 border border-error/20 rounded text-xs text-error">
            <Icon name="AlertTriangle" size={12} />
            <span>RECESSION STATE ACTIVE — Economy modifier at 0.7</span>
          </div>
        )}
        {marginDisabled && (
          <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
            <Icon name="Ban" size={12} />
            <span>Margin trading disabled globally</span>
          </div>
        )}
        {ipoFrozen && (
          <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded text-xs text-warning">
            <Icon name="Lock" size={12} />
            <span>IPO launches frozen</span>
          </div>
        )}
        {!isRecession && !marginDisabled && !ipoFrozen && (
          <div className="flex items-center gap-2 px-3 py-2 bg-success/10 border border-success/20 rounded text-xs text-success">
            <Icon name="CheckCircle" size={12} />
            <span>All systems normal — Bounded volatility active</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default VolatilityIndicator;
